import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { jwtDecode } from 'jwt-decode';

// NOTE: userId and restaurantId from the JWT are for UI convenience only
// (displaying user info, building local routes). For any sensitive API call —
// /addemployee, /addshift, etc. — DO NOT send restaurantId from the frontend.
// The backend must derive it from the JWT claims itself. This prevents IDOR
// attacks where a malicious user could change restaurantId in the request
// body to access another restaurant's data.

const TOKEN_KEY = 'jwt_token';

const AuthContext = createContext(null);

function parseToken(token) {
  try {
    const claims = jwtDecode(token);
    const now = Math.floor(Date.now() / 1000);
    if (claims.exp && claims.exp < now) return null;
    return claims;
  } catch {
    return null;
  }
}

function claimsToState(token, claims) {
  return {
    token,
    userId: claims.userId ?? claims.sub ?? null,
    restaurantId: claims.restaurantId ?? null,
    email: claims.email ?? claims.sub ?? null,
    fullName: claims.fullName ?? null,
    role: claims.role ?? (Array.isArray(claims.roles) ? claims.roles[0] : null) ?? null,
  };
}

const LOGGED_OUT = { token: null, userId: null, restaurantId: null, email: null, fullName: null, role: null };

export function AuthProvider({ children }) {
  const [authState, setAuthState] = useState(() => {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (!stored) return LOGGED_OUT;
    const claims = parseToken(stored);
    if (!claims) {
      localStorage.removeItem(TOKEN_KEY);
      return LOGGED_OUT;
    }
    return claimsToState(stored, claims);
  });

  const login = useCallback((token) => {
    const claims = parseToken(token);
    if (!claims) return;
    localStorage.setItem(TOKEN_KEY, token);
    setAuthState(claimsToState(token, claims));
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthState(LOGGED_OUT);
  }, []);

  const value = useMemo(
    () => ({ ...authState, isAuthenticated: !!authState.token, login, logout }),
    [authState, login, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}